import root from "window-or-global";

import {
    ClientLogger,
    type LogLevel,
    type ClientLoggerOptions,
} from "@atg-paw-utils/client-logger";
// circular dependendency @atg-shared/log --> @atg-shared/feature-experimentation --> @atg-shared/log. Will be resolved when removing the feature toggle for new logger
// eslint-disable-next-line @nx/enforce-module-boundaries
import {Flags, subscribeToDecision} from "@atg-shared/feature-experimentation";
import atgFetch from "@atg-shared/fetch";

import legacyLogger, {serverLogPlugin as legacyServerLogPlugin} from "./legacy";

import type {AtgResponse} from "@atg-shared/fetch-types";

const LOGGER_METHODS = [
    "error",
    "warn",
    "info",
    "debug",
    "flush",
    "destroy",
    "setContext",
    "setLogEndpoint",
] as const;

const rewriteResponseStatus = (response: AtgResponse<unknown>) => ({
    ...response,
    status: response.meta.code,
});

// The client-logger expects a fetch function that returns a Response object, but our atgFetch function returns an AtgResponse object. This wrapper adapts the response to include a status property for compatibility with client-logger.
const fetchWithStatus = async (...args: Parameters<typeof atgFetch>) => {
    const response = await atgFetch(...args);
    return rewriteResponseStatus(response);
};

const fetchWithoutAuth = async (url: string, init?: RequestInit) =>
    fetchWithStatus(url, init, null);

const UNINITIALIZED_LOGGER_MESSAGE =
    "Logger was used before it was initialized! Call initLogger() during app startup.";
type LogFn = (
    message: string,
    ...data: Array<string | number | boolean | Record<string, unknown> | Array<unknown>>
) => void;
export type Logger = {
    error: LogFn;
    warn: LogFn;
    info: LogFn;
    debug: LogFn;
    flush: () => void;
    destroy: () => void;
    setContext: (
        newContext:
            | Record<string, unknown>
            | ((ctx?: Record<string, unknown>) => Record<string, unknown>),
    ) => void;
    setLogEndpoint: (endpoint: string) => void;
};

function reportUninitializedLoggerUsage(...args: unknown[]) {
    // eslint-disable-next-line no-console
    console.error(UNINITIALIZED_LOGGER_MESSAGE, ...args);
}

function createUninitializedLogger(): Logger {
    return LOGGER_METHODS.reduce((acc, method) => {
        acc[method] = (...args: unknown[]) => reportUninitializedLoggerUsage(...args);
        return acc;
    }, {} as Logger);
}

const loggerState = {
    logger: createUninitializedLogger(),
};

function getLoggerState() {
    return loggerState;
}

const logger: Logger = {
    error: (...args) => getLoggerState().logger.error(...args),
    warn: (...args) => getLoggerState().logger.warn(...args),
    info: (...args) => getLoggerState().logger.info(...args),
    debug: (...args) => getLoggerState().logger.debug(...args),
    flush: (...args) => getLoggerState().logger.flush(...args),
    destroy: (...args) => getLoggerState().logger.destroy(...args),
    setContext: (newContext) => {
        currentContext =
            typeof newContext === "function" ? newContext(currentContext) : newContext;
        getLoggerState().logger.setContext(currentContext);
    },
    setLogEndpoint: (endpoint: string) => {
        getLoggerState().logger.setLogEndpoint(endpoint);
    },
};

let currentContext: Record<string, unknown> | undefined;

type InitLoggerParams = {
    application: ClientLoggerOptions["application"];
    logLevel: LogLevel;
    context?: ClientLoggerOptions["context"];
    devMode?: ClientLoggerOptions["devMode"];
};

const appendContextToArgs = (
    args: Parameters<LogFn>,
    context?: Record<string, unknown> | undefined,
): Parameters<LogFn> => {
    if (!context) {
        return args;
    }
    const data = args[1];

    if (typeof data === "object" && !Array.isArray(data)) {
        return [args[0], {...context, ...data}, ...args.slice(2)];
    }
    return [args[0], context, ...args.slice(1)];
};

function createLegacyLoggerAdapter(log: typeof legacyLogger): Logger {
    return {
        error: (...args) => log.error(...appendContextToArgs(args, currentContext)),
        warn: (...args) => log.warn(...appendContextToArgs(args, currentContext)),
        info: (...args) => log.info(...appendContextToArgs(args, currentContext)),
        debug: (...args) => log.debug(...appendContextToArgs(args, currentContext)),
        flush: () => undefined,
        destroy: () => undefined,
        setContext: (
            newContext:
                | Record<string, unknown>
                | ((ctx?: Record<string, unknown>) => Record<string, unknown>),
        ) => {
            currentContext =
                typeof newContext === "function"
                    ? newContext(currentContext)
                    : newContext;
        },
        // no-op for legacy logger
        setLogEndpoint: () => undefined,
    };
}

function invokeSafely<T extends (...args: any[]) => void>(
    fn: T,
    args: Parameters<T>,
): void {
    try {
        fn(...args);
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error("Error invoking function safely:", error);
    }
}

function createCompositeLogger(loggerSinks: Array<Logger>): Logger {
    return LOGGER_METHODS.reduce((acc, method) => {
        acc[method] = (...args: any[]) => {
            // @ts-expect-error: Dynamic method invocation on logger sinks
            loggerSinks.forEach((loggerSink) => invokeSafely(loggerSink[method], args));
        };
        return acc;
    }, {} as Logger);
}

/**
 * Initializes the app-specific logger instance.
 * During migration this dual-writes to both the PAW logger and the legacy loglevel logger.
 */
export function initLogger({application, logLevel, context, devMode}: InitLoggerParams) {
    const loggers: Array<Logger> = [];
    currentContext = context;

    let pawLoggerInstance: Logger | undefined;
    let legacyLoggerInstance: Logger | undefined;

    // subscribe to changes in the decision to update loggers dynamically, it allows toggling between the PAW logger and the legacy logger based on feature flags
    subscribeToDecision(Flags.PAW_NEW_CLIENT_LOGGER, ({variationKey, variables}) => {
        const pawLoggerEnabled = variationKey === "on";
        const legacyLoggerEnabled =
            variationKey === "off" || variables.legacy_logger_active;

        // enable PAW logger if the feature flag is on and it doesn't already exist
        if (pawLoggerEnabled && !pawLoggerInstance) {
            pawLoggerInstance = new ClientLogger({
                logEndpoint: `${root.clientConfig.tepCloud}/client-log-proxy/api/v1/logs`,
                application,
                logLevel,
                license: root.marketConfig.market,
                brand: "ATG",
                fetchFn: fetchWithStatus,
                fallbackFetchFn: fetchWithoutAuth,
                context: currentContext,
                devMode,
            });
            loggers.push(pawLoggerInstance);
        }

        // disable PAW logger if the feature flag is off and it has already been initialized
        if (!pawLoggerEnabled && pawLoggerInstance) {
            pawLoggerInstance.destroy();
            loggers.splice(loggers.indexOf(pawLoggerInstance), 1);
            pawLoggerInstance = undefined;
        }

        // enable legacy logger if the feature flag is on and it doesn't already exist
        if (
            legacyLoggerEnabled &&
            (!legacyLoggerInstance || !loggers.includes(legacyLoggerInstance))
        ) {
            // the legacyLogServerPlugin must only be initialized once per application instance
            if (!legacyLoggerInstance) {
                // The legacy serverLogPlugin was used only for micro-frontends without `atg-web-` prefix. We keep the old microFE names to not break existing dashboards.
                if (application.startsWith("atg-web-")) {
                    const microFE = application.replace("atg-web-", "");
                    legacyServerLogPlugin(legacyLogger, microFE);
                } else {
                    legacyServerLogPlugin(legacyLogger, "");
                }
                legacyLogger.setLevel?.(logLevel);
                legacyLoggerInstance = createLegacyLoggerAdapter(legacyLogger);
            }
            loggers.push(legacyLoggerInstance);
        }

        if (
            !legacyLoggerEnabled &&
            legacyLoggerInstance &&
            loggers.includes(legacyLoggerInstance)
        ) {
            loggers.splice(loggers.indexOf(legacyLoggerInstance), 1);
        }

        getLoggerState().logger = createCompositeLogger(loggers);
    });

    return logger;
}

export default logger;
export {default as serializeError} from "./serializeError";
